// File: src/core-pages/Feedback.js
import React, { useEffect, useState } from 'react';
import { db, auth } from '../Security/firebase-config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import './Feedback.css';
import CustomAlert from '../components/core-components/CustomAlert';

const FEEDBACK_CATEGORIES = [
  'Bug Report',
  'Suggestions',
  'Requests',
  'Praise',
  'Free Trial issues/requests',
  'Billing and Subscription Support',
];

const Feedback = () => {
  const location = useLocation();
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [charCount, setCharCount] = useState(0);

  const [requestType, setRequestType] = useState('');
  const [supportTopic, setSupportTopic] = useState('');
  const [contactMethod, setContactMethod] = useState('');
  const DISCORD_INVITE_URL = "https://discord.gg/GrCvYvqFVF";
  const [contactValue, setContactValue] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);

  const REQUEST_SUBOPTIONS = [
    'Request to increase max team member size',
    'Request increase max teams i can be on',
    'Other',
  ];

  const SUPPORT_SUBOPTIONS = [
    'Payment or subscription question',
    'Phone verification question',
    'Free trial eligibility question',
    'Billing page or payer setup issue',
    'Other support question',
  ];

  const requiresConsentAndContact =
    category === 'Requests' &&
    (requestType === 'Request to increase max team member size' ||
      requestType === 'Request increase max teams i can be on');


  // Local state for CustomAlert
  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',     // 'info' | 'success' | 'warning' | 'error'
    position: 'top',  // 'top' (matches your CustomAlert.js)
  });

  const showAlert = (message, type = 'info', position = 'top') => {
    setAlertState({ open: true, message, type, position });
    // optional auto-dismiss after 4s
    window.clearTimeout(showAlert._t);
    showAlert._t = window.setTimeout(() => {
      setAlertState(s => ({ ...s, open: false }));
    }, 4000);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefilledCategory = params.get('category');

    if (prefilledCategory && FEEDBACK_CATEGORIES.includes(prefilledCategory)) {
      setCategory(prefilledCategory);
      setRequestType('');
      setSupportTopic('');
      setContactMethod('');
      setContactValue('');
      setConsentGiven(false);
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation — replace alert(...)
    if (!category || !message.trim()) {
      showAlert('Please fill in all fields.', 'warning', 'top');
      return;
    }
    
    // Requests: require suboption
    if (category === 'Requests' && !requestType) {
      showAlert('Please choose a Request Type.', 'warning', 'top');
      return;
    }

    if (category === 'Billing and Subscription Support' && !supportTopic) {
      showAlert('Please choose a support topic.', 'warning', 'top');
      return;
    }

    // If user chose one of the quota-related request types:
    if (requiresConsentAndContact) {
      if (!contactMethod) {
        showAlert('Please choose your preferred contact method.', 'warning', 'top');
        return;
      }
      if (!contactValue.trim()) {
        showAlert(
          contactMethod === 'email'
            ? 'Please enter your email address.'
            : 'Please enter your Discord username.',
          'warning',
          'top'
        );
        return;
      }
      if (!consentGiven) {
        showAlert('You must consent before submitting this request.', 'warning', 'top');
        return;
      }
    }


    try {
      // ——— gather user info ———
      const currentUser = auth.currentUser;
      let userUid        = null;
      let usernameForDB  = 'Guest';
      let loggedIn       = false;
      let hasSpecialCode = false;

      if (currentUser) {
        loggedIn = true;
        userUid  = currentUser.uid;

        const profileSnap = await getDoc(doc(db, 'users', userUid));
        if (profileSnap.exists()) {
          const data = profileSnap.data();
          usernameForDB  = data.username || userUid;
          hasSpecialCode = !!data.specialCode;
        } else {
          usernameForDB = userUid;
        }

        // (You had a second fetch — kept to preserve your logic)
        const userSnap = await getDoc(doc(db, 'users', userUid));
        if (userSnap.exists()) {
          hasSpecialCode = !!userSnap.data().specialCode;
        }
      }

      // ——— write feedback with extra fields ———
      await addDoc(collection(db, 'feedback'), {
        category,
        requestType: category === 'Requests' ? requestType : null,
        supportTopic:
          category === 'Billing and Subscription Support' ? supportTopic : null,

        preferredContactMethod: requiresConsentAndContact ? contactMethod : null,
        preferredContact: requiresConsentAndContact ? contactValue.trim() : null,
        consentGiven: requiresConsentAndContact ? consentGiven : null,
        consentText: requiresConsentAndContact
          ? 'I hereby consent to the Valovate staff reaching out to me via my chosen preferred method of contact in order to go over the request.'
          : null,

        message,
        timestamp: serverTimestamp(),
        userUid,
        username: usernameForDB,
        loggedIn,
        hasSpecialCode,
        status: 'Unviewed',
      });

      setMessage('');
      setCharCount(0);
      setCategory('');
      setRequestType('');
      setSupportTopic('');
      setContactMethod('');
      setContactValue('');
      setConsentGiven(false);


      // Success — use CustomAlert instead of <p className="success-msg">
      showAlert('Thanks for your feedback!', 'success', 'top');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      // Error — replace alert(...)
      showAlert('Failed to submit feedback. Please try again later.', 'error', 'top');
    }
  };

  return (
    <div className="feedback-page">
      {/* CustomAlert at the top-level so it overlays nicely */}
      {alertState.open && (
        <CustomAlert
          message={alertState.message}
          type={alertState.type}
          position={alertState.position}
          onClose={() => setAlertState(s => ({ ...s, open: false }))}
        />
      )}

      <div className="feedback-content">
        <h1 className="feedback-title">Feedback</h1>
        <form onSubmit={handleSubmit} className="feedback-form">
          <label htmlFor="category">Select Category:</label>
          <select
            id="category"
            value={category}
            onChange={(e) => {
              const next = e.target.value;
              setCategory(next);

              // reset request-related fields when switching category
              if (next !== 'Requests') {
                setRequestType('');
                setContactMethod('');
                setContactValue('');
                setConsentGiven(false);
              }

              if (next !== 'Billing and Subscription Support') {
                setSupportTopic('');
              }
            }}
          >
            <option value="">-- Choose One --</option>
            {FEEDBACK_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          
          {category === 'Requests' && (
            <>
              <label htmlFor="requestType">Request Type:</label>
              <select
                id="requestType"
                value={requestType}
                onChange={(e) => {
                  const next = e.target.value;
                  setRequestType(next);

                  // reset contact/consent when changing suboption
                  setContactMethod('');
                  setContactValue('');
                  setConsentGiven(false);
                }}
              >
                <option value="">-- Choose One --</option>
                {REQUEST_SUBOPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              {requiresConsentAndContact && (
                <>
                  <label htmlFor="contactMethod">Preferred Method of Contact:</label>
                  <select
                    id="contactMethod"
                    value={contactMethod}
                    onChange={(e) => {
                      setContactMethod(e.target.value);
                      setContactValue('');
                    }}
                  >
                    <option value="">-- Choose One --</option>
                    <option value="discord">Discord</option>
                    <option value="email">Email</option>
                  </select>

                  {contactMethod && (
                    <>
                      {contactMethod === "discord" && (
                        <div className="Feedback-discord-join">
                          <p style={{ margin: "0 0 0.5rem 0" }}>
                            Please join our Discord server so admins can contact you:
                          </p>
                          <a
                            href={DISCORD_INVITE_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="Feedback-discord-link"
                          >
                            {DISCORD_INVITE_URL}
                          </a>
                        </div>
                      )}

                      <label htmlFor="contactValue">
                        {contactMethod === "discord" ? "Discord Username:" : "Email Address:"}
                      </label>

                      <input
                        id="contactValue"
                        type={contactMethod === "email" ? "email" : "text"}
                        value={contactValue}
                        onChange={(e) => setContactValue(e.target.value)}
                        placeholder={
                          contactMethod === "discord"
                            ? "e.g. username#1234 or @username"
                            : "e.g. you@example.com"
                        }
                      />
                    </>
                  )}

                  <label className="Feedback-consent-row">
                    <input
                      type="checkbox"
                      checked={consentGiven}
                      onChange={(e) => setConsentGiven(e.target.checked)}
                    />
                    <span>
                      I hereby consent to the Valovate staff reaching out to me via my chosen preferred method of contact in order to go over the request.
                    </span>
                  </label>
                </>
              )}
            </>
          )}

          {category === 'Billing and Subscription Support' && (
            <>
              <label htmlFor="supportTopic">Support Topic:</label>
              <select
                id="supportTopic"
                value={supportTopic}
                onChange={(e) => setSupportTopic(e.target.value)}
              >
                <option value="">-- Choose One --</option>
                {SUPPORT_SUBOPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <div className="Feedback-support-note">
                Use this category for payment or subscription questions, phone
                verification issues, and free-trial eligibility concerns.
              </div>
            </>
          )}


          <label htmlFor="message">Your Message (max 300 characters):</label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => {
              if (e.target.value.length <= 300) {
                setMessage(e.target.value);
                setCharCount(e.target.value.length);
              }
            }}
          />

          <div className="char-count">{charCount}/300</div>
          <button type="submit">Submit Feedback</button>

          {/* Remove this, the CustomAlert handles success feedback now */}
          {/* {submitted && <p className="success-msg">Thanks for your feedback!</p>} */}
        </form>
      </div>
    </div>
  );
};

export default Feedback;
